import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, getToken } from "../../lib/api";
import { AuthImg, Field, bLabel } from "./parts";
import { ArrowLeft, Download, Send, X, Loader2, Trash2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function ClientReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bundle, setBundle] = useState(null);
  const [history, setHistory] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [message, setMessage] = useState("Attached is the latest vineyard field report. Please let us know if you have any questions.");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/scouting/reports/${id}`);
    setBundle(data);
    const h = await api.get(`/scouting/reports?ranch_number=${encodeURIComponent(data.report.ranch_number)}`);
    setHistory(h.data);
    if (data.report.client_id) {
      const c = await api.get("/clients");
      const cl = c.data.find((x) => x.id === data.report.client_id);
      if (cl?.email) setRecipients(cl.email);
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!bundle) return <div className="p-8 font-bold uppercase text-sm">Loading…</div>;
  const { report, photos, completed_tasks, upcoming_tasks } = bundle;
  const editable = report.status === "draft";

  const saveField = async (upd) => {
    const { data } = await api.put(`/scouting/reports/${id}`, upd);
    setBundle((b) => ({ ...b, report: data }));
  };

  const downloadPdf = async () => {
    const token = getToken();
    const res = await fetch(`${API}/scouting/reports/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Field_Report_${report.vineyard_name}_${report.report_date}_v${report.version}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/scouting/reports/${id}/send`, {
        recipients: recipients.split(",").map((r) => r.trim()).filter(Boolean), message });
      setSendOpen(false);
      load();
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="client-report">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => navigate("/scouting")} className="h-11 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><ArrowLeft size={15} /> Scouting</button>
        <div className="flex gap-2">
          <button data-testid="download-pdf-btn" onClick={downloadPdf} className="h-11 px-4 bg-white border border-[#1F3D34] text-[#1F3D34] font-bold uppercase text-xs flex items-center gap-2"><Download size={15} /> PDF</button>
          {report.status !== "sent" && (
            <button data-testid="send-report-btn" onClick={() => setSendOpen(true)} className="h-11 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-2"><Send size={15} /> Send Report</button>
          )}
          {user?.role === "admin" && (
            <button data-testid="delete-report-btn"
              onClick={async () => {
                if (!window.confirm(`Delete this report${report.status === "sent" ? " — it will disappear from the client portal" : ""}? This cannot be undone.`)) return;
                try { await api.delete(`/scouting/reports/${id}`); navigate("/scouting"); }
                catch (err) { alert(err.response?.data?.detail || "Could not delete report"); }
              }}
              className="h-11 px-4 bg-white border border-red-400 text-red-600 font-bold uppercase text-xs flex items-center gap-2 hover:bg-red-50">
              <Trash2 size={15} /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Report web view */}
      <div className="bg-white border border-stone-200 p-6 sm:p-10 space-y-6">
        <div className="border-b-2 border-[#7C9673] pb-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">VineOps</div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#1F3D34]" style={{ fontFamily: "'Fraunces', serif" }}>Vineyard Field Report</h1>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-sm">
            {[["Client", report.client_name || "—"], ["Vineyard", report.vineyard_name], ["Inspection Date", report.report_date], ["Prepared By", report.prepared_by]].map(([k, v]) => (
              <div key={k}><div className="text-[9px] uppercase font-bold text-stone-400">{k}</div><div className="font-semibold">{v}</div></div>
            ))}
          </div>
          <div className="mt-2 flex gap-2 items-center">
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${report.status === "sent" ? "bg-[#7C9673] text-white" : "bg-amber-100 text-amber-800"}`}>{report.status} · v{report.version}</span>
            {report.sent_at && <span className="text-xs text-stone-500">Sent {report.sent_at.slice(0, 10)} by {report.sent_by} to {(report.recipients || []).join(", ")}</span>}
          </div>
        </div>

        <section>
          <h2 className="font-bold uppercase text-sm text-[#1F3D34] mb-2">Vineyard Overview</h2>
          {editable ? (
            <textarea data-testid="overview-edit" defaultValue={report.overview} rows={4}
              onBlur={(e) => e.target.value !== report.overview && saveField({ overview: e.target.value })}
              className="w-full border border-stone-300 p-3 text-sm leading-relaxed" />
          ) : <p className="text-sm leading-relaxed">{report.overview}</p>}
        </section>

        {report.block_rows?.length > 0 && (
          <section>
            <h2 className="font-bold uppercase text-sm text-[#1F3D34] mb-2">Phenology &amp; Growth</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-stone-200">
                <thead className="bg-[#1F3D34] text-white text-xs uppercase">
                  <tr>{["Block", "Variety", "Phenology", "Shoot Length", "Growth", "Vigor"].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {report.block_rows.map((r, i) => (
                    <tr key={r.block_id || `${r.block}-${r.variety}` || i} className="border-b border-stone-100 odd:bg-stone-50/50">
                      <td className="p-2 font-semibold">{r.block || "—"}</td>
                      <td className="p-2">{r.variety || "—"}</td>
                      <td className="p-2">{r.phenology || "—"}</td>
                      <td className="p-2 font-mono">{r.shoot_length != null ? `${r.shoot_length}"` : "—"}{r.growth_change != null ? ` (+${r.growth_change}")` : ""}</td>
                      <td className="p-2">{r.growth || "—"}</td>
                      <td className="p-2">{r.vigor || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section>
          <h2 className="font-bold uppercase text-sm text-[#1F3D34] mb-2">Vineyard Health</h2>
          {editable ? (
            <textarea data-testid="health-edit" defaultValue={report.health_summary} rows={3}
              onBlur={(e) => e.target.value !== report.health_summary && saveField({ health_summary: e.target.value })}
              className="w-full border border-stone-300 p-3 text-sm leading-relaxed" />
          ) : <p className="text-sm leading-relaxed">{report.health_summary}</p>}
        </section>

        {upcoming_tasks.length > 0 && (
          <section>
            <h2 className="font-bold uppercase text-sm text-[#1F3D34] mb-2">Upcoming Work</h2>
            {upcoming_tasks.map((t) => (
              <div key={t.id} className="text-sm py-0.5">• {t.title}{t.block_labels?.length ? ` — ${t.block_labels.map(bLabel).join(", ")}` : ""}{(t.scheduled_date || t.due_date) ? ` (${t.scheduled_date || t.due_date})` : ""}</div>
            ))}
          </section>
        )}

        <section>
          <h2 className="font-bold uppercase text-sm text-[#1F3D34] mb-2">Management Priorities</h2>
          {editable ? (
            <textarea data-testid="priorities-edit" defaultValue={(report.priorities || []).join("\n")} rows={5}
              onBlur={(e) => saveField({ priorities: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })}
              className="w-full border border-stone-300 p-3 text-sm leading-relaxed" placeholder="One priority per line" />
          ) : (
            <ol className="text-sm space-y-1 list-decimal list-inside">{(report.priorities || []).map((p, i) => <li key={`${i}-${p}`}>{p}</li>)}</ol>
          )}
        </section>

        {photos.length > 0 && (
          <section>
            <h2 className="font-bold uppercase text-sm text-[#1F3D34] mb-2">Photos</h2>
            <div className="grid grid-cols-2 gap-3">
              {photos.map((p) => (
                <div key={p.id}>
                  <AuthImg photoId={p.id} className="w-full h-48 object-cover border border-stone-200" />
                  <div className="text-xs text-stone-600 mt-1"><b>{p.block_label ? bLabel(p.block_label) : ""}</b>{p.caption ? ` — ${p.caption}` : ""}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {completed_tasks.length > 0 && (
          <section>
            <h2 className="font-bold uppercase text-sm text-[#1F3D34] mb-2">Work Completed</h2>
            {completed_tasks.map((t) => <div key={t.id} className="text-sm py-0.5">✓ {t.title}{t.block_labels?.length ? ` — ${t.block_labels.map(bLabel).join(", ")}` : ""}</div>)}
          </section>
        )}
      </div>

      {/* History */}
      {history.length > 1 && (
        <div className="bg-white border border-stone-200 p-4">
          <div className="font-bold uppercase text-sm mb-2">Field Reports — {report.vineyard_name}</div>
          {history.map((r) => (
            <button key={r.id} onClick={() => navigate(`/scouting/report/${r.id}`)}
              className={`w-full text-left flex justify-between py-1.5 text-sm border-b border-stone-100 last:border-0 hover:bg-stone-50 ${r.id === id ? "font-bold" : ""}`}>
              <span>{r.report_date} · v{r.version}</span>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${r.status === "sent" ? "bg-[#7C9673] text-white" : "bg-stone-200"}`}>{r.status}</span>
            </button>
          ))}
        </div>
      )}

      {/* Send modal */}
      {sendOpen && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white border border-stone-200 w-full max-w-md p-6 space-y-4" data-testid="send-report-modal">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Send Report</h3>
              <button onClick={() => setSendOpen(false)}><X size={20} /></button>
            </div>
            <Field label="Recipients (comma-separated emails)">
              <input data-testid="send-recipients-input" value={recipients} onChange={(e) => setRecipients(e.target.value)} className="w-full h-12 px-3 border border-stone-300" placeholder="client@email.com" />
            </Field>
            <Field label="Message">
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="w-full border border-stone-300 p-3 text-sm" />
            </Field>
            <div className="text-xs text-stone-500">The report will be marked as sent and locked. Download the PDF and attach it to your email.</div>
            <button data-testid="confirm-send-btn" onClick={send} disabled={busy}
              className="w-full h-12 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2">
              {busy && <Loader2 size={16} className="animate-spin" />} Mark Sent &amp; Record
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
